export interface PolygonVertex {
  x: number;
  y: number;
}

/**
 * Ray-casting point-in-polygon test. Works for any simple (non-self-intersecting)
 * polygon in a shared 2D coordinate space (e.g. area-local meters).
 */
export function isPointInsidePolygon(point: PolygonVertex, vertices: PolygonVertex[]): boolean {
  if (vertices.length < 3) return false;

  let inside = false;

  for (
    let index = 0, previousIndex = vertices.length - 1;
    index < vertices.length;
    previousIndex = index++
  ) {
    const vertex = vertices.at(index)!;
    const previousVertex = vertices.at(previousIndex)!;
    const edgeX = vertex.x - previousVertex.x;
    const edgeY = vertex.y - previousVertex.y;
    const pointX = point.x - previousVertex.x;
    const pointY = point.y - previousVertex.y;
    const crossProduct = edgeX * pointY - edgeY * pointX;
    const edgeTolerance = 1e-7 * Math.max(1, Math.hypot(edgeX, edgeY));
    const isOnEdge =
      Math.abs(crossProduct) <= edgeTolerance &&
      point.x >= Math.min(vertex.x, previousVertex.x) &&
      point.x <= Math.max(vertex.x, previousVertex.x) &&
      point.y >= Math.min(vertex.y, previousVertex.y) &&
      point.y <= Math.max(vertex.y, previousVertex.y);
    if (isOnEdge) return true;

    const crossesHorizontalRay =
      vertex.y > point.y !== previousVertex.y > point.y &&
      point.x <
        ((previousVertex.x - vertex.x) * (point.y - vertex.y)) / (previousVertex.y - vertex.y) +
          vertex.x;

    if (crossesHorizontalRay) inside = !inside;
  }

  return inside;
}

/** Shoelace formula; returns the polygon's area in the same squared units as its coordinates. */
export function calculatePolygonArea(vertices: PolygonVertex[]): number {
  if (vertices.length < 3) return 0;

  const twiceArea = vertices.reduce((sum, vertex, index) => {
    const nextVertex = vertices.at((index + 1) % vertices.length)!;
    return sum + vertex.x * nextVertex.y - nextVertex.x * vertex.y;
  }, 0);

  return Math.abs(twiceArea) / 2;
}
